import React, { useEffect, useState, useCallback, useRef } from "react";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { Select, MenuItem, Skeleton } from "@mui/material";
import CheckIconBig from "@styles/all-images/icons/check-icon-big.svg";
import NotificationIconBig from "@styles/all-images/icons/notification-icon-big.svg";
import actionsHandler from "@/store/actions/actionHandler";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch, RootState } from "@/store";
import { toast } from "react-toastify";
import { SelectChangeEvent } from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import StandaloneChatDrawer, {
  StandaloneChatDrawerType,
} from "../LiveChat/StandaloneChatDrawer";
import {
  ChatTypeText,
  NotificationType,
  NotificationTypeIcon,
  NotificationTypeUrl,
} from "@/constants/enums/notification-types.enum";
import dateHandler from "@/composables/dateUtil";
import { useNavigate } from "react-router-dom";
import CalendarIcon from "@styles/all-images/icons/Calander.svg";
import { setUnreadNotificationCount } from "@/store/slices/authSlice";
import { ChatType } from "@/constants/enums/notification-types.enum";
import { DocumentInfo } from "../LiveChat/StandaloneChatDrawer/ChatComponent";
import { Document } from "../T4/DocumentMasterTab";
import { DocumentApprovalStatus } from "@/constants/enums/document-approval-status.enum";

interface Data {
  user_id: number;
  document_id: number;
  tab: number;
  sequence: number;
  chat_type: ChatType;
  sender_id: number;
  sender_company_id: number;
  message_id: number;
  room_id: number;
  document_data: {
    sequence: number;
    tab: number;
    document_id: number;
    document_name: string;
    status: DocumentApprovalStatus;
  };
}

interface Notification {
  id: number;
  user: number;
  title: string;
  message: string;
  mark_as_read: boolean;
  data: Data;
  notification_type: NotificationType;
  from_user: number;
  created_at: string;
  updated_at: string;
}

const NotificationSkeleton = () => (
  <>
    {[1, 2, 3].map((item) => (
      <div
        key={item}
        className="tw-bg-white tw-border tw-border-[#EAECF0] tw-rounded-lg tw-p-4 tw-mb-[10px] tw-flex tw-items-center tw-justify-between"
      >
        <div className="tw-flex tw-gap-4 tw-items-center tw-w-full">
          <Skeleton variant="circular" width={34} height={34} />
          <div className="tw-flex-1">
            <Skeleton variant="text" width={200} height={24} />
            <Skeleton variant="text" width={300} height={20} />
            <Skeleton variant="text" width={60} height={16} />
          </div>
          <Skeleton variant="circular" width={20} height={20} />
        </div>
      </div>
    ))}
  </>
);

const Notification = () => {
  const { formatDateWithTime } = dateHandler();
  const navigate = useNavigate();

  const dispatch = useDispatch<AppThunkDispatch>();
  const [userTypes, setUserTypes] = useState<
    Array<{ id: number; user_type: string }>
  >([]);
  const [selectedUserType, setSelectedUserType] = useState<string>("all");
  const [notificationList, setNotificationList] = useState<Notification[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isChatNotificationOpen, setIsChatNotificationOpen] = useState(false);
  const [chatUserId, setChatUserId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const unreadNotificationCount = useSelector(
    (state: RootState) => state.auth.unreadNotificationCount,
  );
  const [documentChatUserId, setDocumentChatUserId] = useState<number | null>(
    null,
  );
  const [documentChatDocumentInfo, setDocumentChatDocumentInfo] =
    useState<DocumentInfo | null>(null);
  const [documentChatRoomId, setDocumentChatRoomId] = useState<string | null>(
    null,
  );
  const [isDocumentChatOpen, setIsDocumentChatOpen] = useState(false);
  const [startDate, endDate] = dateRange;

  const datePickerRef = useRef<any>(null);

  const handleCloseChatNotification = () => {
    setIsChatNotificationOpen(false);
  };

  // Fetch user types
  const fetchUserTypes = useCallback(async () => {
    try {
      const { authenticatedCall } = actionsHandler("notificationUserFilter");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
        }) as any,
      );

      if (response.ok && response.data) {
        setUserTypes(response.data);
      }
    } catch (error) {
      console.error("Error fetching user types:", error);
    }
  }, [dispatch]);

  // Fetch notifications with filter
  const fetchNotifications = useCallback(
    async (pageNum: number) => {
      try {
        setIsLoading(true);
        const { authenticatedCall } = actionsHandler("notificationList");

        // Build query params
        let queryParams = `page=${pageNum}&limit=10`;

        // Add user type filter if selected
        if (selectedUserType !== "all") {
          queryParams += `&user_type=${selectedUserType}`;
        }

        // Add date range filters if selected with dd/mm/yyyy format
        if (startDate) {
          const formattedStartDate = startDate
            .toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            .split("/")
            .join("/");
          queryParams += `&start_date=${formattedStartDate}`;
        }
        if (endDate) {
          const formattedEndDate = endDate
            .toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            .split("/")
            .join("/");
          queryParams += `&end_date=${formattedEndDate}`;
        }

        const response = await dispatch(
          authenticatedCall({
            method: "get",
            customQuery: queryParams,
          }) as any,
        );

        if (response.ok && response.data) {
          if (pageNum === 1) {
            setNotificationList(response.data.data);
          } else {
            setNotificationList((prev) => [...prev, ...response.data.data]);
          }
          setHasMore(!!response.data.meta.next);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, selectedUserType, startDate, endDate],
  );

  const handleScroll = useCallback(() => {
    if (!containerRef.current || isLoading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      setPage((prev) => prev + 1);
      fetchNotifications(page + 1);
    }
  }, [isLoading, hasMore, page, fetchNotifications]);

  useEffect(() => {
    setPage(1);
    fetchNotifications(1);
  }, [selectedUserType, startDate, endDate]);

  useEffect(() => {
    fetchUserTypes();
  }, [fetchUserTypes]);

  const handleUserTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedUserType(event.target.value);
  };

  const formatTimeAgo = (timestamp: string) => {
    try {
      const now = new Date();
      const formettedDate = formatDateWithTime(timestamp);

      // Parse date in format "DD/MM/YYYY HH:mm A"
      const [datePart, timePart, meridiem] = formettedDate.split(" ");
      const [day, month, year] = datePart.split("/").map(Number);
      const [hours, minutes] = timePart.split(":").map(Number);

      // Adjust hours for PM
      let adjustedHours = hours;
      if (meridiem === "PM" && hours !== 12) {
        adjustedHours = hours + 12;
      } else if (meridiem === "AM" && hours === 12) {
        adjustedHours = 0;
      }

      const date = new Date(year, month - 1, day, adjustedHours, minutes);

      if (isNaN(date.getTime())) {
        console.error("Invalid date:", timestamp);
        return "Invalid date";
      }

      const secondsDiff = Math.floor((now.getTime() - date.getTime()) / 1000);
      const minutesDiff = Math.floor(secondsDiff / 60);
      const hoursDiff = Math.floor(minutesDiff / 60);
      const daysDiff = Math.floor(hoursDiff / 24);
      const weeksDiff = Math.floor(daysDiff / 7);
      const monthsDiff = Math.floor(daysDiff / 30);
      const yearsDiff = Math.floor(daysDiff / 365);

      if (yearsDiff > 0) return `${yearsDiff}y`;
      if (monthsDiff > 0) return `${monthsDiff}mo`;
      if (weeksDiff > 0) return `${weeksDiff}w`;
      if (daysDiff > 0) return `${daysDiff}d`;
      if (hoursDiff > 0) return `${hoursDiff}h`;
      if (minutesDiff > 0) return `${minutesDiff}m`;
      return `${secondsDiff}s`;
    } catch (error) {
      console.error("Error parsing date:", error);
      return "Invalid date";
    }
  };

  const handleMarkAsRead = async (
    notificationId: number,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    event.stopPropagation();
    try {
      const { authenticatedCall } = actionsHandler("markAsReadNotification");
      const response = await dispatch(
        authenticatedCall({
          method: "put",
          customParams: [`${notificationId}/`],
          payload: {
            mark_as_read: true,
          },
        }),
      );

      if (response.ok) {
        // Update notification count in store
        const currentCount = response.data.notification_un_read_count;
        dispatch(setUnreadNotificationCount(currentCount));

        // Refresh the notifications list
        setPage(1);
        fetchNotifications(1);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("markAllAsRead");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
        }) as any,
      );

      if (response.ok) {
        // Refresh the notifications list
        setPage(1);
        fetchNotifications(1);
        const currentCount = response.data.notification_un_read_count;
        dispatch(setUnreadNotificationCount(currentCount));
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationClick = async (
    notification: Notification,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    event.stopPropagation();

    if (!notification.mark_as_read) {
      await handleMarkAsRead(notification.id, event);
    }

    if (!notification.data) {
      toast.warning("Notification data is not available");
      // return navigate(NotificationTypeUrl[notification.notification_type]);
    }

    let url = NotificationTypeUrl[notification.notification_type];

    switch (notification.notification_type) {
      case NotificationType.T3ApproveDocument:
      case NotificationType.T3RejectDocument:
        url = url
          .replace(":tab", notification.data.tab.toString())
          .replace(":sequence", (notification.data.sequence - 1).toString());
        break;

      case NotificationType.ChatNotification:
        if (!notification.data.sender_id) {
          return toast.warning("Chat is not available for this notification");
        }

        if (notification.data.chat_type === ChatType.Document) {
          return handleChat(notification);
        }

        url = url
          .replace(
            ":chat_type",
            ChatTypeText[notification.data.chat_type as ChatType],
          )
          .replace(
            ":id",
            notification.data.chat_type === ChatType.User
              ? notification.data.sender_id.toString()
              : notification.data.room_id.toString(),
          );

        if (notification.data.chat_type !== ChatType.User) {
          url = url.replace("&company_id=:company_id", "");
        } else {
          url = url.replace(
            ":company_id",
            notification.data.sender_company_id.toString(),
          );
        }
        break;

      default:
        if (notification.data.document_id) {
          url = url.replace(":id", notification.data.document_id.toString());
        } else if (notification.data.user_id) {
          url = url.replace(":id", notification.data.user_id.toString());
        }
    }

    navigate(url);
  };

  const handleChat = (notification: Notification) => {
    if (notification.data?.document_data?.document_id) {
      console.log("notification: ", notification);
      setDocumentChatUserId(notification.data.sender_id);
      setDocumentChatDocumentInfo({
        id: notification.data.document_data.document_id,
        title: notification.data.document_data.document_name,
        status: notification.data.document_data.status,
      });
      setDocumentChatRoomId(notification.data.room_id?.toString());
      setIsDocumentChatOpen(true);
    } else {
      toast.warning("Chat is not available for this document");
    }
  };

  useEffect(() => {
    if (!isChatNotificationOpen) {
      setChatUserId(null);
    }
  }, [isChatNotificationOpen]);

  const handleDatePickerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      setPage(1);
      fetchNotifications(1);
    }
  };

  const handleCalendarIconClick = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(true);
    }
  };

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-grid tw-grid-cols-5 tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center tw-col-span-1">
              <a href="/">
                <img src={BackIcon} alt="Back Icon" />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                Notification
              </h1>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tw-p-6 tw-mt-[140px] tw-max-w-[768px] tw-mx-auto">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
          <div className="tw-flex tw-items-center tw-gap-4 from-group">
            <div className="form-group">
              <div className="tw-relative">
                <DatePicker
                  ref={datePickerRef}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={new Date()}
                  onChange={(update: [Date | null, Date | null]) => {
                    setDateRange(update);
                    if (update[0] && update[1]) {
                      datePickerRef.current?.setOpen(false);
                      setPage(1);
                      fetchNotifications(1);
                    }
                  }}
                  className="tw-bg-white tw-rounded-md tw-px-3 tw-py-2 tw-border tw-border-gray-300"
                  placeholderText="Select date range"
                  dateFormat="dd/MM/yyyy"
                  isClearable={false}
                  onKeyDown={handleDatePickerKeyDown}
                />
                <img
                  src={CalendarIcon}
                  alt="Calendar"
                  className="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-w-6 tw-h-6 tw-cursor-pointer"
                  onClick={handleCalendarIconClick}
                />
              </div>
            </div>
            <div className="form-group">
              <Select
                value={selectedUserType}
                onChange={handleUserTypeChange}
                className="tw-bg-white tw-min-w-[150px]"
                size="small"
                sx={{
                  "& .MuiSelect-select": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "200px", // Adjust this value as needed
                  },
                }}
              >
                <MenuItem value="all">All User</MenuItem>
                {userTypes.map((type) => (
                  <MenuItem
                    key={type.id}
                    value={type.id}
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {type.user_type}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
          {Boolean(unreadNotificationCount && unreadNotificationCount > 0) && (
            <p
              className="font-default-primary-color tw-font-medium font-size-base tw-cursor-pointer hover:tw-opacity-80"
              onClick={handleMarkAllAsRead}
            >
              Mark all as read
            </p>
          )}
        </div>
        <div
          ref={containerRef}
          className="tw-mt-3 tw-h-[60vh] tw-overflow-y-auto"
          onScroll={handleScroll}
        >
          {notificationList.length > 0 ? (
            <>
              {notificationList.map(
                (notification: Notification, index: number) => (
                  <div
                    onClick={(event) =>
                      handleNotificationClick(notification, event)
                    }
                    className={`tw-bg-white tw-border tw-border-[#EAECF0] tw-rounded-lg tw-p-4 tw-mb-[10px] tw-flex tw-items-center tw-justify-between 
                    ${!notification.mark_as_read ? "hover:tw-bg-[#ECECED]" : "tw-opacity-70"} 
                    tw-transition-all tw-duration-300 tw-ease-in-out`}
                    key={index}
                  >
                    <div className="tw-flex tw-gap-4 tw-items-center tw-w-full">
                      <div className="tw-text-blue-600 tw-cursor-pointer">
                        <img
                          src={
                            NotificationTypeIcon[notification.notification_type]
                          }
                          alt="Notification Icon"
                          className="tw-w-[34px] tw-h-[34px] tw-min-w-[34px] tw-min-h-[34px] tw-max-w-[34px] tw-max-h-[34px]"
                        />
                      </div>
                      <div className="tw-flex-1">
                        <h2 className="font-size-sm tw-font-bold font-primary-color">
                          {notification.title}
                        </h2>
                        <p
                          className="font-primary-color font-size-sm tw-mt-1"
                          dangerouslySetInnerHTML={{
                            __html: notification.message,
                          }}
                        />
                        <span className="font-gray-color tw-text-sm tw-mt-1">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                      </div>
                      {!notification.mark_as_read && (
                        <div
                          className="tw-cursor-pointer"
                          onClick={(event) =>
                            handleMarkAsRead(notification.id, event)
                          }
                        >
                          <img
                            src={CheckIconBig}
                            alt="Check Icon"
                            className="tw-w-[20px] tw-h-[20px] tw-min-w-[20px] tw-min-h-[20px] tw-max-w-[20px] tw-max-h-[20px] hover:tw-opacity-80"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ),
              )}

              {isLoading && <NotificationSkeleton />}

              {!isLoading && hasMore && (
                <div className="tw-text-center tw-py-4">
                  <span className="tw-text-gray-500">
                    Scroll down to load more
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-[60vh] tw-text-center">
              <div className="tw-p-4 tw-rounded-lg tw-bg-gray-50 tw-w-full">
                <p className="tw-text-gray-500 tw-text-base">
                  {isLoading ? (
                    <NotificationSkeleton />
                  ) : (
                    "No Notification at the moment"
                  )}
                </p>
              </div>
            </div>
          )}

          {!isLoading && !hasMore && notificationList.length > 0 && (
            <div className="tw-text-center tw-py-4 tw-text-gray-500">
              No more notifications
            </div>
          )}
        </div>
      </div>

      {documentChatUserId && (
        <StandaloneChatDrawer
          isChatNotificationOpen={isDocumentChatOpen}
          setIsChatNotificationOpen={setIsDocumentChatOpen}
          documentInfo={documentChatDocumentInfo || undefined}
          documentId={documentChatDocumentInfo?.id || undefined}
          userId={documentChatUserId}
          customChatRoomId={documentChatRoomId}
          type={StandaloneChatDrawerType.DOCUMENT}
        />
      )}
    </div>
  );
};

export default Notification;
